import { collectionProductFragment } from './get-collection-products-query'
import { metafieldFragment } from './utils'

const videoFragment = /* GraphQL */ `
	fragment videosFields on Media {
		... on ExternalVideo {
			id
			host
			originUrl
			embedUrl
			previewImage {
				id
				url
				altText
				width
				height
			}
		}
	}
`

export const imageFragment = /* GraphQL */ `
	fragment imageFields on Image {
		id
		altText
		height
		width
		url
	}
`

const sellingPlanFragment = /* GraphQL */ `
	fragment sellingPlanFields on Product {
		sellingPlanGroups(first: 100) {
			edges {
				node {
					name
					sellingPlans(first: 100) {
						edges {
							node {
								id
								name
								description
								options {
									name
									value
								}
								priceAdjustments {
									adjustmentValue {
										... on SellingPlanFixedPriceAdjustment {
											price {
												amount
												currencyCode
											}
										}
										... on SellingPlanPercentagePriceAdjustment {
											adjustmentPercentage
										}
										... on SellingPlanFixedAmountPriceAdjustment {
											adjustmentAmount {
												amount
												currencyCode
											}
										}
									}
								}
							}
						}
					}
				}
			}
		}
	}
`

const productFragment = /* GraphQL */ `
	fragment productFields on Product {
		id
		handle
		availableForSale
		title
		productType
		vendor
		tags
		description
		descriptionHtml
		options {
			id
			name
			values
		}
		priceRange {
			maxVariantPrice {
				amount
				currencyCode
			}
			minVariantPrice {
				amount
				currencyCode
			}
		}
		requiresSellingPlan

		collections(first: 250) {
			nodes {
				id
				title
				handle
			}
		}

		...sellingPlanFields

		notForSale: metafield(namespace: "custom", key: "not_for_sale") {
			...metafield
		}
		howToUse: metafield(namespace: "product", key: "how_to_use") {
			...metafield
		}
		brand: metafield(namespace: "my_fields", key: "brand") {
			...metafield
		}
		highlights: metafield(namespace: "my_fields", key: "highlights") {
			...metafield
		}
		promotionalDescription: metafield(namespace: "my_fields", key: "promotional_description") {
			...metafield
		}

		ingredients: metafield(namespace: "product", key: "ingredients") {
			...metafield
		}
		avgRating: metafield(namespace: "loox", key: "avg_rating") {
			...metafield
		}
		numReviews: metafield(namespace: "loox", key: "num_reviews") {
			...metafield
		}

		videos: media(first: 250) {
			nodes {
				...videosFields
			}
		}

		images(first: 250) {
			pageInfo {
				hasNextPage
				hasPreviousPage
			}
			edges {
				node {
					...imageFields
				}
			}
		}

		variants(first: 250) {
			pageInfo {
				hasNextPage
				hasPreviousPage
			}
			nodes {
				id
				title
				sku
				weight
				weightUnit
				availableForSale
				requiresShipping
				quantityAvailable
				selectedOptions {
					name
					value
				}

				compareAtPriceV2 {
					amount
					currencyCode
				}

				priceV2 {
					amount
					currencyCode
				}

				image {
					...imageFields
				}

				isSample: metafield(namespace: "my_fields", key: "is_sample") {
					...metafield
				}

				hide: metafield(namespace: "custom", key: "hide") {
					...metafield
				}

				swatch: metafield(namespace: "my_fields", key: "swatch") {
					id
					key
					type
					reference {
						... on MediaImage {
							image {
								url
							}
						}
					}
				}
			}
		}
	}

	${sellingPlanFragment}
`

export const getProductByIdQuery = /* GraphQL */ `
	query getProductById($id: ID!) {
		product(id: $id) {
			...collectionProduct
		}
	}

	${collectionProductFragment}
	${metafieldFragment}
`

export const getAllSubscriptionBoxes = /* GraphQL */ `
	query getAllSubscriptionBoxes {
		products(query: "tag:'Subscription Box' status:active", first: 20) {
			nodes {
				id
				title
				tags
				variants(first: 1) {
					nodes {
						id
						price
						compareAtPrice
					}
				}
				planDetails: metafield(namespace: "bb_subscriptions_api", key: "plan_details") {
					...metafield
				}
				sellingPlanGroups(first: 1) {
					nodes {
						name
						sellingPlans(first: 1) {
							nodes {
								id
								billingPolicy {
									... on SellingPlanRecurringBillingPolicy {
										intervalCount
										interval
									}
								}
								deliveryPolicy {
									... on SellingPlanRecurringDeliveryPolicy {
										intervalCount
										interval
									}
								}
							}
						}
					}
				}
			}
		}
	}

	${metafieldFragment}
`

export const getProductByIdQueryAdmin = /* GraphQL */ `
	query getProductById($id: ID!) {
		product(id: $id) {
			sellingPlanGroups(first: 10) {
				edges {
					node {
						sellingPlans(first: 10) {
							edges {
								node {
									name
									id
									options
									billingPolicy {
										... on SellingPlanRecurringBillingPolicy {
											intervalCount
											interval
										}
									}
								}
							}
						}
					}
				}
			}
		}
	}
`

const getProductQuery = /* GraphQL */ `
	query getProductBySlug($slug: String!) {
		product(handle: $slug) {
			...productFields
		}
	}

	${productFragment}
	${metafieldFragment}
	${videoFragment}
	${imageFragment}
`
export const getProductListQuery = /* GraphQL */ `
	query getProductList($variantIds: [ID!]!) {
		nodes(ids: $variantIds) {
			... on ProductVariant {
				id
				product {
					id
					title
					handle
					...collectionProduct
				}
			}
		}
	}

	${collectionProductFragment}
	${metafieldFragment}
`

export default getProductQuery
